import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiProvider } from '@providers/common/api.service';
import { consultasCivar, consultasCivarUrl } from '@common/constants';
import { FrontFiltersService } from '@providers/front-filters-service/front-filters.service';

/**
 * @name ConsultasCivarService
 * @desc Servicio usado para hacer las peticiones de las vistas de consultas
 */
@Injectable({
  providedIn: 'root'
})
export class ConsultasCivarService {
  private api = inject(ApiProvider);
  private frontFiltersService = inject(FrontFiltersService);


  /**
   * @name getVinesLastUpdate
   * @desc Metodo que consulta y regresa la fecha de la ultima actualizacion de los datos que se estan mostrando en Consulta CD,
   * o null si el servidor no la regresa
   */
  getVinesLastUpdate(): Observable<Date | null> {
    return this.api.post(consultasCivar.getVinesLastUpdate, {}, consultasCivarUrl).pipe(
      map((records: any) => (records?.Fec_SnapShots ? new Date(records.Fec_SnapShots) : null)),
    );
  }

  /**
   * @name getVines
   * @desc Metodo que regresa el numero de vines que hay dada una consulta y/o unos filtros
   */
  getVines(consult: any, dealer = 0) {
    const fields = consult.cols.concat(consult.rows).toString();
    return this.api.post(consultasCivar.vines, {
      dealer,
      nueva: 1,
      filtros: consult.filters === "" ? 0 : 1,
      campos: fields + ",",
      filtroDrillDown: "",
      detalle: 0,
      id_Cnsc_Consulta: 0,
      filtrosXML: consult.filters
    }, consultasCivarUrl);
  }

  /**
   * @name getVinesNavigateBy
   * @desc Metodo que regresa el numero de vines que hay dada una consulta y un criterio de busqueda
   */
  getVinesNavigateBy(drillDown: string, fields: string, filters: any, dealer = 0) {
    return this.api.post(consultasCivar.vines, {
      dealer,
      nueva: 1,
      filtros: !!filters && filters !== "" ? 1 : 0,
      campos: fields + ",",
      filtroDrillDown: drillDown,
      detalle: 0,
      id_Cnsc_Consulta: 0,
      filtrosXML: filters || ""
    }, consultasCivarUrl);
  }


  // CONSULTAS
  /**
   * @name getAll
   * @desc Metodo que regresa todas las consultas almacenadas por un usuario
   */
  getAll() {
    return this.api.post(
      consultasCivar.verTodas,
      {},
      consultasCivarUrl

    );
  }

  /**
   * @name saveConsult
   * @desc Metodo que guarda una consulta en la bd
   */
  saveConsult(name: any, consult: any) {
    return this.api.post(consultasCivar.guardar, {
      "desc_Nombre": name,
      "bit_Default": consult.bit_Default || 0,
      "filtros": consult.filters === "" ? 0 : 1,
      "campos": 1,
      "ordenamiento": consult.cols.map((v: any, i: any) => {
        return v + ";" + (consult.sort ? consult.sort.cols[i].toUpperCase() : "ASC")
      }).concat(consult.rows.map((v: any, i: any) => {
        return v + ";" + (consult.sort ? consult.sort.rows[i].toUpperCase() : "ASC")
      })).join("|") + "|",
      "valores": this.frontFiltersService.serializeForBackend(consult),
      "filtrosXML": consult.filters,
      "columnas": consult.cols.join("|") + "|",
      "renglones": consult.rows.join("|") + "|"
    }, consultasCivarUrl);
  }

  /**
   * @name updateConsult
   * @desc Metodo que actualiza una consulta en la bd
   */
  updateConsult(consult: any) {
    return this.api.post(consultasCivar.actualizar, {
      "id_Cnsc_Consulta": consult.id || consult.id_Cnsc_Consulta,
      "desc_Nombre": consult.name,
      "bit_Default": consult.bit_Default,
      "filtros": consult.filters === "" ? "0" : "1",
      "campos": 1,
      "ordenamiento": consult.cols.map((v: any, i: any) => {
        return v + ";" + (consult.sort ? consult.sort.cols[i].toUpperCase() : "ASC")
      }).concat(consult.rows.map((v: any, i: any) => {
        return v + ";" + (consult.sort ? consult.sort.rows[i].toUpperCase() : "ASC")
      })).join("|") + "|",
      "valores": this.frontFiltersService.serializeForBackend(consult),
      "filtrosXML": consult.filters,
      "columnas": consult.cols.join("|") + "|",
      "renglones": consult.rows.join("|") + "|"
    }, consultasCivarUrl);
  }

  /**
   * @name deleteConsults
   * @desc Metodo que elimina una consulta de la bd
   */
  deleteConsults(ids: string[]) {
    return this.api.post(consultasCivar.eliminar, {
      "id_Cnsc_Consulta": ids.join(",0;") + ",0;"
    }, consultasCivarUrl);
  }

  /**
   * @name parseConsultFields
   * @desc Metodo que llena rows/cols/sort/frontFilters de un consult a partir del
   * Renglones/Columnas devueltos por el backend (formato "Campo;orden;v1~v2|...")
   */
  parseConsultFields(consult: any, data: any): void {
    consult.sort = {
      rows: [],
      cols: [],
    };
    consult.rows = data.Renglones.slice(0, -1)
      .split('|')
      .map((val) => {
        const valArr = val.split(';');
        consult.sort.rows.push(valArr[1].toLowerCase());
        this.frontFiltersService.registerFromServerField(
          consult,
          valArr[0],
          valArr[2],
        );
        return valArr[0];
      });
    consult.cols = data.Columnas.slice(0, -1)
      .split('|')
      .map((val) => {
        const valArr = val.split(';');
        consult.sort.cols.push(valArr[1].toLowerCase());
        this.frontFiltersService.registerFromServerField(
          consult,
          valArr[0],
          valArr[2],
        );
        return valArr[0];
      });
  }

  /**
   * @name getById
   * @desc Metodo que regresa los detalles de una consulta dado su id
   */
  getById(id: any) {
    return this.api.post(consultasCivar.verUna, {
      "id_Cnsc_Consulta": id,
      "id_Cnsc_Idioma": 1
    }, consultasCivarUrl);
  }

  getDefault() {
    return this.api.post(
      consultasCivar.verDefault,
      {},
      consultasCivarUrl

    );
  }

  saveDefault(id: any) {
    return this.api.post(consultasCivar.guardarDefault, {
      "id_Cnsc_Consulta": id,
    }, consultasCivarUrl);
  }

  // PUBLICAR
  /**
   * @name getPublishedUsers
   * @desc Metodo que regresa los usuarios a los que se les ha publicado una consulta
   */
  getPublishedUsers(id: any) {
    return this.api.post(consultasCivar.verUsuarios, {
      "idConsulta": id
    }, consultasCivarUrl);
  }

  /**
   * @name publishConsult
   * @desc Metodo que guarda la publicacion de una consulta
   */
  publishConsult(id: number, name: string, idTipoPubl: number, users: string) {
    return this.api.post(consultasCivar.publicar, {
      "id_Cnsc_Consulta": id,
      "id_TipoPublicacion": idTipoPubl,
      "desc_Nombre": name,
      "usuariosSeg": users
    }, consultasCivarUrl);
  }

  /**
   * @name searchUsers
   * @desc Metodo que busca usuarios
   */
  searchUsers(idTipo: number, searchStr: string) {
    return this.api.post(consultasCivar.buscarUsuarios, {
      "id_Tipo": idTipo,
      "descripcion": searchStr
    }, consultasCivarUrl);
  }

  // FILTROS
  /**
   * @name getFilter
   * @desc Metodo que regresa los valores de un filtro
   */
  getFilter(key: any, filters: any = {}) {
    const body = {
      opcion: this.filterOptions[key],
      ...filters
    };
    return firstValueFrom(this.api.post(consultasCivar.filtros, body, consultasCivarUrl));
  }

  private filterOptions: any = {
    "Cve_Cliente": 1,
    "Cve_Marca": 2,
    "Cve_Familia": 3,
    "Num_Remision": 4,
    "Desc_Modelo": 5,
    "Cve_Linea": 6,
    "Num_PullOrder": 8,
    "Cve_Distribuidor": 9,
    "Desc_PerteneceCDM": 10,
    "Desc_VINImpExpDom": 11,
    "Cve_Catalogo": 12,
    "Cve_Zona": 13,
    "Desc_Accesorizado": 14,
    "Cve_Destino": 15,
    "Id_Cnsc_Source": 17,
    "Desc_Detenciones": 19
  }

  /**
   * @name getLAB
   * @desc Metodo que regresa los valores del filtro de LAB
   */
  getLAB() {
    return firstValueFrom(this.api.post(
      consultasCivar.lab,
      {},
      consultasCivarUrl

    ));
  }

  /**
   * @name getRange
   * @desc Metodo que regresa los valores de los filtros que sean de un rango de números (Antigüedad en Company Stock, ...)
   */
  getRange() {
    return firstValueFrom(this.api.post(
      consultasCivar.range,
      {},
      consultasCivarUrl

    ));
  }
}
